import { QueryParameters as FilteredComponentsQueryParams } from "@faire/web-api/api/v2/layout/filtered-components/post";
import { useQuery as useFilteredComponentsQuery } from "@faire/web-api/api/v2/layout/filtered-components/post-hooks";
import { useQuery_escape_hatch as useFiltersQuery } from "@faire/web-api/api/v2/layout/filters/post-hooks";
import { useQuery as useNavigationTreeQuery } from "@faire/web-api/api/v2/layout/navigation-tree/get-hooks";
import { IFilterablePaginatedSection } from "@faire/web-api/indigofair/data/IFilterablePaginatedSection";
import { IGetSDUIFilteredComponentsRequest } from "@faire/web-api/indigofair/data/IGetSDUIFilteredComponentsRequest";
import { IGetSDUIFiltersRequest } from "@faire/web-api/indigofair/data/IGetSDUIFiltersRequest";
import { IPaginationData } from "@faire/web-api/indigofair/data/IPaginationData";
import { keepPreviousData } from "@tanstack/react-query";
import { UserStore } from "@faire/retailer/stores/domain/UserStore";
import { getSettingShouldDisplayRetrievalAndRankingDebugInfo } from "@faire/retailer/settings/getSettingShouldDisplayRetrievalAndRankingDebugInfo";

import { useSDUIFilterablePaginatedSectionFilters } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Filters/useSDUIFilterablePaginatedSectionFilters";
import { useSDUIFilterablePaginatedSectionState } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Filters/useSDUIFilterablePaginatedSectionState";
import { getNavigationTreeHashCode } from "@faire/retailer/serialized-data/getNavigationTreeHashCode";
import { returnRetrievalRankingDebugInfo } from "@faire/retailer-shared/search/loggedInBrandLeanProductTilesRequest";

export const useFilterablePaginatedSectionQuery = (
  filterablePaginatedSection: IFilterablePaginatedSection
) => {
  const { oneIndexedPageNumber } = useSDUIFilterablePaginatedSectionState();
  const filterKeys = useSDUIFilterablePaginatedSectionFilters();
  const filteredComponentsRequest =
    filterablePaginatedSection.get_s_d_u_i_filtered_components;
  const navigationTreeRequest =
    filterablePaginatedSection.get_s_d_u_i_navigation_tree_request;
  const shouldRequestDebugInfo = returnRetrievalRankingDebugInfo(
    UserStore.get().user,
    getSettingShouldDisplayRetrievalAndRankingDebugInfo()
  );

  const { isFetching: isFilterLoading, data: filtersResponse } =
    useFiltersQuery(
      IGetSDUIFiltersRequest.build({
        page_identifier: filteredComponentsRequest?.page_identifier,
        filter_keys: filterKeys,
      }),
      { staleTime: Infinity, placeholderData: keepPreviousData }
    );

  const {
    isFetching: isFilteredComponentsFetching,
    isLoading: isFilteredComponentsLoading,
    isRefetching: isFilteredComponentsRefetching,
    data: filteredComponentsData,
  } = useFilteredComponentsQuery(
    IGetSDUIFilteredComponentsRequest.build({
      page_identifier: filteredComponentsRequest?.page_identifier,
      filter_keys: filterKeys,
      pagination_data: IPaginationData.build({
        page_size: filteredComponentsRequest?.pagination_data?.page_size,
        page_number: oneIndexedPageNumber,
      }),
      ...(shouldRequestDebugInfo
        ? { return_debug_info: shouldRequestDebugInfo }
        : undefined),
    }),
    FilteredComponentsQueryParams.build({}),
    {
      staleTime: Infinity,
      placeholderData: keepPreviousData,
    }
  );

  const { data: navigationTreeData, isFetching: isNavigationTreeLoading } =
    useNavigationTreeQuery(
      {
        navigation_path: navigationTreeRequest?.navigation_path ?? "",
        navigation_tree_hash_code: getNavigationTreeHashCode(),
      },
      { staleTime: Infinity, placeholderData: keepPreviousData }
    );

  return {
    isFilterLoading,
    isFilteredComponentsFetching,
    isFilteredComponentsLoading,
    isFilteredComponentsRefetching,
    isNavigationTreeLoading,
    filtersResponse,
    filteredComponentsData,
    navigationTreeData,
  };
};
